import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input,
    Col, Row, Button, Spinner, Table, Badge, Card, CardBody, CardHeader
} from 'reactstrap';
import InputMask from 'react-input-mask';
import Swal from 'sweetalert2';
import { AlunoContext } from '../../context/AlunoContext';
import { EditAlunoType, VendaDetalhadaAlunoType } from '../../context/hooks/useAluno';
import { CursoContext } from '../../context/CursoContext';
import useUser from '../../context/hooks/useUser';

type ViaCepResponse = {
    cep?: string;
    logradouro?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
    erro?: boolean;
};

interface Props {
    isOpen: boolean;
    toggle: () => void;
    aluno: EditAlunoType | null;
    contratoId?: number;
    cursoId?: number;
    onAlunoAtualizado?: () => void;
}

export const EditAlunoModal: React.FC<Props> = ({
    isOpen,
    toggle,
    aluno,
    contratoId,
    cursoId,
    onAlunoAtualizado
}) => {
    const { updateAluno, alterarCursoAluno, handleGetVendaByAlunoId, handleGetHistoricoAlbumPorAluno } = useContext(AlunoContext);
    const { handleGetCursoByContratoId, listCursoByContrato } = useContext(CursoContext);
    const { validateCpf } = useUser();
    const [formData, setFormData] = useState<EditAlunoType | null>(null);
    const [novoCursoId, setNovoCursoId] = useState<number | undefined>(undefined);
    const [cursoLoading, setCursoLoading] = useState(false);
    const [vendaData, setVendaData] = useState<VendaDetalhadaAlunoType | null>(null);
    const [vendaLoading, setVendaLoading] = useState(false);
    const [historicoAlbum, setHistoricoAlbum] = useState<any[]>([]);
    const [historicoLoading, setHistoricoLoading] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const [zipLoading, setZipLoading] = useState(false);
    const [zipError, setZipError] = useState<string | null>(null);
    const lastZipFetchedRef = useRef<string>('');
    const zipAbortRef = useRef<AbortController | null>(null);
    const zipDebounceRef = useRef<number | null>(null);

    const onlyDigits = (s?: string) => (s ?? '').replace(/\D/g, '');

    useEffect(() => {
        if (isOpen) {
            setFormData(aluno);
            setNovoCursoId(cursoId);
            setZipError(null);
            setZipLoading(false);
            lastZipFetchedRef.current = '';
            if (zipAbortRef.current) zipAbortRef.current.abort();

            if (contratoId) {
                setCursoLoading(true);
                handleGetCursoByContratoId(contratoId).finally(() => setCursoLoading(false));
            }

            if (aluno?.id) {
                setVendaLoading(true);
                handleGetVendaByAlunoId(aluno.id)
                    .then((res) => setVendaData(res))
                    .catch(() => setVendaData(null))
                    .finally(() => setVendaLoading(false));

                setHistoricoLoading(true);
                handleGetHistoricoAlbumPorAluno(aluno.id)
                    .then((res) => setHistoricoAlbum(res))
                    .catch(() => setHistoricoAlbum([]))
                    .finally(() => setHistoricoLoading(false));
            } else {
                setVendaData(null);
                setHistoricoAlbum([]);
            }
        } else {
            setVendaData(null);
            setHistoricoAlbum([]);
        }
    }, [isOpen, aluno]);

    useEffect(() => {
        const zipDigits = onlyDigits(formData?.zipCode);

        if (zipDigits.length !== 8) {
            if (zipDebounceRef.current) window.clearTimeout(zipDebounceRef.current);
            return;
        }

        zipDebounceRef.current = window.setTimeout(() => {
            fetchViaCep(zipDigits);
        }, 350);

        return () => {
            if (zipDebounceRef.current) window.clearTimeout(zipDebounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.zipCode]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!formData) return;
        const { name, value, type, checked } = e.target;
        if (name === 'zipCode') setZipError(null);
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const applyViaCep = (data: ViaCepResponse) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                state: prev.state?.trim() ? prev.state : data.uf || '',
                city: prev.city?.trim() ? prev.city : data.localidade || '',
                neighborhood: prev.neighborhood?.trim() ? prev.neighborhood : data.bairro || '',
                address: prev.address?.trim() ? prev.address : data.logradouro || '',
            };
        });
    };

    const fetchViaCep = async (zipDigits: string) => {
        if (zipDigits.length !== 8) return;
        if (lastZipFetchedRef.current === zipDigits) return;

        if (zipAbortRef.current) zipAbortRef.current.abort();
        const controller = new AbortController();
        zipAbortRef.current = controller;

        setZipLoading(true);
        setZipError(null);

        try {
            const resp = await fetch(`https://viacep.com.br/ws/${zipDigits}/json/`, {
                method: 'GET',
                signal: controller.signal,
            });

            if (!resp.ok) throw new Error();

            const json = (await resp.json()) as ViaCepResponse;

            if (json.erro) {
                setZipError('CEP não encontrado. Verifique e tente novamente.');
                return;
            }

            lastZipFetchedRef.current = zipDigits;
            applyViaCep(json);
        } catch (err: any) {
            if (err?.name === 'AbortError') return;
            setZipError('Não foi possível consultar o CEP agora. Tente novamente.');
        } finally {
            setZipLoading(false);
        }
    };

    const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!formData) return;
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData({ ...formData, base64Image: reader.result as string });
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData) return;

        const cursoAlterado = novoCursoId !== undefined && novoCursoId !== cursoId && contratoId;

        if (cursoAlterado) {
            const cursoOk = await alterarCursoAluno(formData.id, contratoId!, novoCursoId!);
            if (!cursoOk) return;
        }

        const ok = await updateAluno(formData);
        if (ok) {
            onAlunoAtualizado?.();
            toggle();
        }
    };

    if (!formData) return null;

    const handleValidateCpf = async () => {
        if (!formData?.cpf) return;

        const rawCpf = formData.cpf.replace(/\D/g, '');
        if (!rawCpf || rawCpf.length !== 11) return;

        const result = await validateCpf(rawCpf);

        if (result?.tipo && result?.id !== formData.id) {
            const confirm = await Swal.fire({
                icon: 'warning',
                title: 'CPF já cadastrado!',
                html: `<b>Tipo:</b> ${result.tipo}<br/><b>Nome:</b> ${result.nome}<br/><br/>Deseja continuar mesmo assim?`,
                showCancelButton: true,
                confirmButtonText: 'Continuar edição',
                cancelButtonText: 'Limpar CPF',
                reverseButtons: true
            });

            if (!confirm.isConfirmed) {
                setFormData({ ...formData, cpf: '' });
            }
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl">
            <ModalHeader toggle={toggle}>Alterar informações do aluno</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <Label className="form-control-label">Foto do aluno</Label>
                            <div className="d-flex align-items-center">
                                <div style={{ marginRight: 15 }}>
                                    {formData.base64Image ? (
                                        <img
                                            src={formData.base64Image}
                                            alt="Foto atual"
                                            style={{
                                                width: 64, height: 64, borderRadius: '50%',
                                                objectFit: 'cover', boxShadow: '0 0 3px rgba(0,0,0,0.3)'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: 64, height: 64, borderRadius: '50%',
                                            background: '#ccc'
                                        }} />
                                    )}
                                </div>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImage}
                                    innerRef={imageInputRef}
                                />
                            </div>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Código de referência</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="referenceCode"
                                    value={formData.referenceCode}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {contratoId && (
                        <Row className="mt-2">
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label">
                                        Curso
                                        {cursoLoading && <Spinner size="sm" className="ml-2" />}
                                    </Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="select"
                                        value={novoCursoId ?? ''}
                                        onChange={e => setNovoCursoId(Number(e.target.value))}
                                        disabled={cursoLoading || listCursoByContrato.length === 0}
                                    >
                                        {listCursoByContrato.length === 0 ? (
                                            <option value="">Nenhum curso disponível</option>
                                        ) : (
                                            listCursoByContrato.map(curso => (
                                                <option key={curso.id} value={curso.id}>
                                                    {curso.name}
                                                </option>
                                            ))
                                        )}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <Row className="mt-2">
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Nome</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">E-mail</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-cpf">CPF</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="cpf"
                                    id="cpf"
                                    value={formData?.cpf || ''}
                                    onChange={handleChange}
                                    onBlur={handleValidateCpf}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-address">Endereço</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="address"
                                    id="address"
                                    value={formData?.address || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-addressNumber">Número</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="addressNumber"
                                    id="addressNumber"
                                    value={formData?.addressNumber || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-neighborhood">Bairro</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="neighborhood"
                                    id="neighborhood"
                                    value={formData?.neighborhood || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label
                                    className="form-control-label d-flex align-items-center justify-content-between"
                                    htmlFor="input-zipCode"
                                >
                                    <span>CEP</span>
                                    {zipLoading && (
                                        <span className="d-inline-flex align-items-center">
                                            <Spinner size="sm" />
                                            <span className="ml-2" style={{ fontSize: 12, opacity: 0.8 }}>Buscando...</span>
                                        </span>
                                    )}
                                </Label>
                                <InputMask
                                    mask="99999-999"
                                    type="text"
                                    name="zipCode"
                                    id="zipCode"
                                    value={formData?.zipCode ?? ''}
                                    placeholder="00000-000"
                                    onChange={handleChange}
                                    className="form-control form-control-alternative"
                                />
                                {zipError && (
                                    <div style={{ color: '#d9534f', fontSize: 12, marginTop: 6 }}>
                                        {zipError}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-state">Estado</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="state"
                                    id="state"
                                    value={formData?.state || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-city">Cidade</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="city"
                                    id="city"
                                    value={formData?.city || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-landline">Telefone fixo</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="landline"
                                    id="landline"
                                    value={formData?.landline || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-telephone_01">Telefone 01</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="telephone_01"
                                    id="telephone_01"
                                    value={formData?.telephone_01 || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-telephone_02">Telefone 02</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="telephone_02"
                                    id="telephone_02"
                                    value={formData?.telephone_02 || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-telephone_03">Telefone 03</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="telephone_03"
                                    id="telephone_03"
                                    value={formData?.telephone_03 || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-telephone_04">Telefone 04</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="telephone_04"
                                    id="telephone_04"
                                    value={formData?.telephone_04 || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-telephone_05">Telefone 05</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="telephone_05"
                                    id="telephone_05"
                                    value={formData?.telephone_05 || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-telephone_06">Telefone 06</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="telephone_06"
                                    id="telephone_06"
                                    value={formData?.telephone_06 || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-facebook">Facebook</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="facebook"
                                    id="facebook"
                                    value={formData?.facebook || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-instagram">Instagram</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="instagram"
                                    id="instagram"
                                    value={formData?.instagram || ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-keepThePhotos">Manteve as fotos?</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    name="keepThePhotos"
                                    id="keepThePhotos"
                                    value={formData?.keepThePhotos ? "true" : "false"}
                                    onChange={e => setFormData({
                                        ...formData,
                                        keepThePhotos: e.target.value === "true"
                                    })}
                                >
                                    <option value="true">Sim</option>
                                    <option value="false">Não</option>
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-hasAlbum">Possui álbum?</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    name="hasAlbum"
                                    id="hasAlbum"
                                    value={formData?.hasAlbum ? "true" : "false"}
                                    onChange={e => setFormData({
                                        ...formData,
                                        hasAlbum: e.target.value === "true"
                                    })}
                                >
                                    <option value="true">Sim</option>
                                    <option value="false">Não</option>
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-numberOfPhotos">Número de fotos</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="numberOfPhotos"
                                    id="numberOfPhotos"
                                    value={formData?.numberOfPhotos || 0}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-observacao">Observação</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="textarea"
                                    rows={3}
                                    name="observacao"
                                    id="observacao"
                                    value={formData?.observacao || ''}
                                    onChange={(e) =>
                                        setFormData((prev) => (prev ? { ...prev, observacao: e.target.value } : prev))
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {vendaLoading ? (
                        <div className="text-center my-3 p-3 bg-light rounded">
                            <Spinner size="sm" color="primary" className="mr-2" />
                            <span className="text-muted">Carregando informações da venda...</span>
                        </div>
                    ) : vendaData ? (
                        <Card className="mt-3 mb-3 border" style={{ borderColor: '#2dce89' }}>
                            <CardHeader className="bg-light d-flex justify-content-between align-items-center py-2">
                                <span className="font-weight-bold" style={{ color: '#2dce89', fontSize: '1rem' }}>
                                    <i className="fa-solid fa-cart-shopping mr-2"></i>
                                    Informações da Venda #{vendaData.vendaId}
                                </span>
                                <Badge color="success" pill className="px-3 py-1">
                                    Vendido
                                </Badge>
                            </CardHeader>
                            <CardBody className="pt-3 pb-2">
                                <Row className="mb-3">
                                    <Col xs="6" md="3" className="mb-2">
                                        <small className="text-muted d-block font-weight-bold">Data da Venda</small>
                                        <span>{vendaData.dataVenda ? new Date(vendaData.dataVenda).toLocaleDateString('pt-BR') : '-'}</span>
                                    </Col>
                                    <Col xs="6" md="3" className="mb-2">
                                        <small className="text-muted d-block font-weight-bold">Valor Negociado</small>
                                        <strong className="text-success" style={{ fontSize: '1.05rem' }}>
                                            {vendaData.valorNegociado != null
                                                ? vendaData.valorNegociado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                : '-'}
                                        </strong>
                                    </Col>
                                    <Col xs="6" md="3" className="mb-2">
                                        <small className="text-muted d-block font-weight-bold">Produto</small>
                                        <span>{vendaData.produtoNome || 'Não informado'}</span>
                                    </Col>
                                    <Col xs="6" md="3" className="mb-2">
                                        <small className="text-muted d-block font-weight-bold">Vendedor</small>
                                        <span>{vendaData.vendedorNome || 'Não informado'}</span>
                                    </Col>
                                </Row>

                                {vendaData.formasPagamento && vendaData.formasPagamento.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block font-weight-bold mb-1">Forma(s) de Pagamento:</small>
                                        <div className="d-flex flex-wrap">
                                            {vendaData.formasPagamento.map((fp, idx) => (
                                                <div
                                                    key={idx}
                                                    className="p-2 mr-2 mb-2 border rounded bg-white shadow-sm"
                                                    style={{ minWidth: '170px' }}
                                                >
                                                    <div className="font-weight-bold small text-dark">{fp.formaPagamentoNome}</div>
                                                    <div className="text-success small font-weight-bold">
                                                        {fp.valor != null
                                                            ? fp.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                            : '-'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {vendaData.boletos && vendaData.boletos.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-muted d-block font-weight-bold mb-2">Parcelas / Boletos:</small>
                                        <div className="table-responsive" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                            <Table size="sm" hover className="table-bordered text-center align-items-center mb-0">
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th style={{ width: '80px' }}>Parcela</th>
                                                        <th>Valor</th>
                                                        <th>Vencimento</th>
                                                        <th>Status</th>
                                                        <th>Nosso Número</th>
                                                        <th>Linha Digitável</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {vendaData.boletos.map((b) => (
                                                        <tr key={b.id}>
                                                            <td><strong>{b.parcela}/{b.totalParcelas}</strong></td>
                                                            <td>{b.valor != null ? b.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</td>
                                                            <td>{b.vencimento ? new Date(b.vencimento).toLocaleDateString('pt-BR') : '-'}</td>
                                                            <td>
                                                                <Badge color={b.status === 'Pago' ? 'success' : 'warning'} pill>
                                                                    {b.status}
                                                                </Badge>
                                                            </td>
                                                            <td><small>{b.nossoNumero || '-'}</small></td>
                                                            <td style={{ maxWidth: '180px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                <small title={b.linhaDigitavel || ''}>{b.linhaDigitavel || '-'}</small>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    ) : null}

                    {historicoLoading ? (
                        <div className="text-center my-3 p-3 bg-light rounded">
                            <Spinner size="sm" color="info" className="mr-2" />
                            <span className="text-muted">Carregando histórico do álbum...</span>
                        </div>
                    ) : (
                        <Card className="mt-3 mb-3 border" style={{ borderColor: '#5e72e4' }}>
                            <CardHeader className="bg-light d-flex justify-content-between align-items-center py-2">
                                <span className="font-weight-bold" style={{ color: '#5e72e4', fontSize: '1rem' }}>
                                    <i className="fa-solid fa-book-open mr-2"></i>
                                    Histórico de Relatórios e Vendedores do Álbum
                                </span>
                                <Badge color="primary" pill className="px-3 py-1">
                                    {historicoAlbum.length} registro(s)
                                </Badge>
                            </CardHeader>
                            <CardBody className="p-0">
                                {historicoAlbum.length === 0 ? (
                                    <div className="p-3 text-center text-muted small">
                                        Nenhum relatório de venda registrado para este formando até o momento.
                                    </div>
                                ) : (
                                    <div className="table-responsive" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                        <Table size="sm" hover className="table-bordered text-center align-items-center mb-0">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>Data</th>
                                                    <th>Documento</th>
                                                    <th>Fotos</th>
                                                    <th>Kit</th>
                                                    <th>Status</th>
                                                    <th>Tipo</th>
                                                    <th>Dt. Dev.</th>
                                                    <th>Cód. Vendedor</th>
                                                    <th>Nome Vendedor</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historicoAlbum.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.data ? new Date(item.data).toLocaleDateString('pt-BR') : '-'}</td>
                                                        <td><strong>{item.documento || '-'}</strong></td>
                                                        <td>{item.fotos ?? 0}</td>
                                                        <td>{item.kit ? 'True' : 'False'}</td>
                                                        <td>
                                                            <span className="font-weight-bold" style={{ color: '#32325d' }}>
                                                                {item.statusLabel || '-'}
                                                            </span>
                                                        </td>
                                                        <td>{item.tipo || '-'}</td>
                                                        <td>{item.dtDevolucao ? new Date(item.dtDevolucao).toLocaleDateString('pt-BR') : '-'}</td>
                                                        <td>{item.vendedorId ? String(item.vendedorId).padStart(6, '0') : '-'}</td>
                                                        <td>{item.vendedorNome || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    )}

                    <div className="d-flex justify-content-end mt-3">
                        <Button style={{ background: '#585858', color: '#fff' }} type="submit">
                            Salvar alterações
                        </Button>
                        <Button
                            style={{ background: '#d9534f', color: '#fff' }}
                            className="ml-2"
                            onClick={toggle}
                        >
                            Cancelar
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default EditAlunoModal;
